import type { ReactNode } from 'react';

import { emailValidation } from '@eneco/common/validation/emailRegex';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Checkbox, Heading, InputEmail, InputTelephone, Stack, Text } from '@eneco/sparky';
import {
  type DefaultValues,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormHandleSubmit,
  type UseFormRegister,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';

import { Header } from '../Header/Header';
import { Layout } from '../Layout/Layout';
import { NextButton } from '../NextButton/NextButton';

type TextFieldContent = {
  label?: string;
  placeholder?: string;
  hint?: string;
};

type ContactDetailsFieldProps<TFieldValues extends FieldValues> = {
  register: UseFormRegister<TFieldValues>;
  error?: string;
  field?: TextFieldContent;
};

type ContactDetailsOptInProps<TFieldValues extends FieldValues> = {
  register: UseFormRegister<TFieldValues>;
  title?: string;
  content?: string;
  headingAs?: 'h2' | 'h3';
  checkboxes: {
    name: FieldPath<TFieldValues>;
    label?: string;
  }[];
};

/** Dutch landline or mobile number, 10 digits, optional separator, no country code. */
export const DUTCH_PHONE_PATTERN = /^(?:0[0-57-9]\d{2}[-\s]?\d{6}|0[0-57-9]\d[-\s]?\d{7}|06[-\s]?\d{8})$/;

export const ContactDetailsPhoneField = <TFieldValues extends FieldValues>({
  register,
  error,
  field,
}: ContactDetailsFieldProps<TFieldValues>) => (
  <InputTelephone
    {...register('phoneNumber' as FieldPath<TFieldValues>)}
    label={field?.label ?? ''}
    placeholder={field?.placeholder}
    hint={field?.hint}
    autoComplete="tel"
    error={error}
  />
);

export const ContactDetailsEmailField = <TFieldValues extends FieldValues>({
  register,
  error,
  field,
  note,
  isGrouped = true,
}: ContactDetailsFieldProps<TFieldValues> & { note?: string; isGrouped?: boolean }) => {
  const emailField = (
    <InputEmail
      {...register('emailAddress' as FieldPath<TFieldValues>)}
      label={field?.label ?? ''}
      placeholder={field?.placeholder}
      hint={field?.hint}
      autoComplete="email"
      error={error}
    />
  );

  if (!isGrouped) return emailField;

  return (
    <Stack.Item>
      {emailField}
      {note && (
        <Box paddingTop="4">
          <Text size="BodyS" color="textLowEmphasis">
            <RichText html={note} />
          </Text>
        </Box>
      )}
    </Stack.Item>
  );
};

export const ContactDetailsOptIn = <TFieldValues extends FieldValues>({
  register,
  title,
  content,
  headingAs = 'h3',
  checkboxes,
}: ContactDetailsOptInProps<TFieldValues>) => (
  <>
    <Stack.Item>
      <Stack direction="column" gap="4">
        <Heading as={headingAs} size="XS">
          {title}
        </Heading>
        <RichText html={content} />
      </Stack>
    </Stack.Item>

    <Stack.Item>
      <Stack direction="column" gap="2">
        {checkboxes.map(checkbox => (
          <Checkbox key={checkbox.name} label={checkbox.label ?? ''} {...register(checkbox.name)} />
        ))}
      </Stack>
    </Stack.Item>
  </>
);

type FieldText = {
  value?: string;
};

type FormFieldText = {
  value?: TextFieldContent & {
    requiredMessage?: string;
    validationMessage?: string;
  };
};

export type ContactDetailsTextualData = {
  contactDetails?: {
    phoneNumberFormField?: FormFieldText;
    emailAddressFormField?: FormFieldText;
    emailAddressText?: FieldText;
  };
  content?: {
    title?: { value: string };
    text?: FieldText;
    nextStepText?: FieldText;
    notification?: {
      value?: {
        title?: string;
        content?: string;
      };
    };
  };
  optIn?: {
    title?: FieldText;
    content?: FieldText;
    checkBoxProductOffersFirstPartyContent?: FieldText;
  };
};

export type ContactDetailsFormValues = {
  phoneNumber: string;
  emailAddress?: string;
  C1?: boolean;
};

export const useContactDetailsForm = <TFieldValues extends ContactDetailsFormValues>(
  textualData: ContactDetailsTextualData | undefined,
  defaultValues: { phoneNumber?: string; emailAddress?: string; C1?: boolean },
) => {
  const phoneField = textualData?.contactDetails?.phoneNumberFormField?.value;
  const emailField = textualData?.contactDetails?.emailAddressFormField?.value;

  const formSchema = yup.object({
    phoneNumber: yup
      .string()
      .required(phoneField?.requiredMessage)
      .matches(DUTCH_PHONE_PATTERN, phoneField?.validationMessage),
    emailAddress: emailValidation({
      emailMessage: emailField?.validationMessage,
      requiredMessage: emailField?.requiredMessage,
    }),
    C1: yup.boolean(),
  });

  return useForm<TFieldValues>({
    mode: 'onSubmit',
    defaultValues: defaultValues as DefaultValues<TFieldValues>,
    resolver: typedYupResolver<TFieldValues>(formSchema),
  });
};

const fieldErrorMessage = (error?: { message?: unknown }) =>
  typeof error?.message === 'string' ? error.message : undefined;

export const ContactDetailsFormFields = <TFieldValues extends ContactDetailsFormValues>({
  register,
  errors,
  textualData,
  showOptIn = true,
}: {
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  textualData?: ContactDetailsTextualData;
  showOptIn?: boolean;
}) => (
  <>
    <ContactDetailsPhoneField
      register={register}
      error={fieldErrorMessage(errors.phoneNumber)}
      field={textualData?.contactDetails?.phoneNumberFormField?.value}
    />
    <ContactDetailsEmailField
      register={register}
      error={fieldErrorMessage(errors.emailAddress)}
      field={textualData?.contactDetails?.emailAddressFormField?.value}
      note={textualData?.contactDetails?.emailAddressText?.value}
    />
    {showOptIn && (
      <ContactDetailsOptIn
        register={register}
        title={textualData?.optIn?.title?.value}
        content={textualData?.optIn?.content?.value}
        checkboxes={[
          {
            name: 'C1' as FieldPath<TFieldValues>,
            label: textualData?.optIn?.checkBoxProductOffersFirstPartyContent?.value,
          },
        ]}
      />
    )}
  </>
);

export const SALESFORCE_LEAD_RECORD_TYPE_ID = '0125I000000cKH0QAM';

export const createSalesforceLeadContact = ({
  firstName,
  surnamePreposition,
  salutation,
  optIn,
  surname,
  postalCode,
  phoneNumber,
  emailAddress,
  houseNumber,
  houseNumberSuffix,
}: {
  firstName?: string;
  surnamePreposition?: string;
  salutation?: string;
  optIn?: boolean;
  surname?: string;
  postalCode?: string;
  phoneNumber?: string;
  emailAddress?: string;
  houseNumber?: string | number;
  houseNumberSuffix?: string | null;
}) => ({
  FirstName__c: firstName ?? '',
  Prefix__c: surnamePreposition ?? '',
  Gender__c: salutation === 'mevr' ? 'v' : 'm',
  Email_opt_in__c: optIn ? 'true' : 'false',
  LastName__c: surname ?? '',
  Postal_Code__c: postalCode ?? '',
  SuppliedPhone: phoneNumber,
  SuppliedEmail: emailAddress ?? '',
  HouseNo__c: houseNumber?.toString() ?? '',
  HouseNoAdd__c: houseNumberSuffix || '',
  RecordTypeId: SALESFORCE_LEAD_RECORD_TYPE_ID,
});

export const LeadContactDetailsShell = <TFieldValues extends FieldValues>({
  textualData,
  handleSubmit,
  hasError,
  isLoading,
  children,
}: {
  textualData?: ContactDetailsTextualData;
  handleSubmit: UseFormHandleSubmit<TFieldValues>;
  hasError: boolean;
  isLoading: boolean;
  children: ReactNode;
}) => (
  <Layout.Content variant="A" handleSubmit={handleSubmit as UseFormHandleSubmit<FieldValues>}>
    <Header>
      <Header.Title>{textualData?.content?.title?.value}</Header.Title>
      {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
    </Header>
    {children}
    {hasError && (
      <TrackedNotificationBox
        isAlert
        variant="error"
        title={textualData?.content?.notification?.value?.title ?? ''}
        text={<RichText html={textualData?.content?.notification?.value?.content} />}
      />
    )}
    <NextButton isLoading={isLoading}>{textualData?.content?.nextStepText?.value}</NextButton>
  </Layout.Content>
);
